import { ApiClient } from "../api-client";
import { ApiClientError } from "../errors";
import { ApiRouteType } from "../types";
import schemaValidators from "./schema-validators";

export default class SAMLClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
	}

	createRelyingPartyRegistration = async ({
		createRequestBody,
	}: {
		createRequestBody: ApiRouteType<"saml", "createRelyingPartyRegistration">;
	}) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration`, {
				credentials: "include",
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					...createRequestBody,
				}),
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getRelyingPartyRegistration = async ({ registrationId }: { registrationId: string }) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}`,
				{
					credentials: "include",
					method: "GET",
				},
			);

			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getRelyingPartyRegistrations = async ({ instituteId, enabled }: { instituteId: string; enabled?: boolean }) => {
		try {
			const searchParams = new URLSearchParams();
			searchParams.set("instituteId", instituteId);
			if (enabled !== undefined) {
				searchParams.set("enabled", enabled.toString());
			}

			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration?${searchParams.toString()}`,
				{
					credentials: "include",
					method: "GET",
				},
			);

			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.getRelyingPartyRegistrations.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	updateRelyingPartyRegistration = async ({
		registrationId,
		updateRequestBody,
	}: {
		registrationId: string;
		updateRequestBody: ApiRouteType<"saml", "updateRelyingPartyRegistration">;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}`,
				{
					credentials: "include",
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						...updateRequestBody,
					}),
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	addRoleMapping = async ({
		registrationId,
		attribute,
		privilegeGroupId,
	}: {
		registrationId: string;
		attribute: string;
		privilegeGroupId: number;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/roleMapping`,
				{
					credentials: "include",
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						attribute,
						privilegeGroupId,
					}),
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	deleteRoleMapping = async ({ registrationId, roleMappingId }: { registrationId: string; roleMappingId: number }) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/roleMapping/${roleMappingId}`,
				{
					credentials: "include",
					method: "DELETE",
					headers: { "Content-Type": "application/json" },
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getRoleMappingPresets = async ({ registrationId }: { registrationId: string }) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/presets`,
				{
					credentials: "include",
					method: "GET",
					headers: { "Content-Type": "application/json" },
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.getRoleMappingPresets.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
